/**
 * LE GRAPHE AVANT / APRÈS ET LES SECTIONS CONFIGURABLES D'UN BILAN, hors du rendu.
 *
 * Un score ventilé (`metadata.standard/current`) porte déjà ses deux états : le
 * départ et ce que les mesures en ont laissé. `before_after_doughnut` les pose
 * côte à côte, à la même échelle, et c'est le vide de l'anneau « après » qui
 * montre le gain.
 *
 * UNE SEULE LECTURE POUR L'ÉCRAN ET POUR LE RAPPORT : `beforeAfterSeries` rend
 * ce que les deux dessinent, chiffres et couleurs compris. Ce qui est projeté
 * est ce qui est envoyé (cf. acgReport.js).
 */
import { DEFAULT_UNIT } from '../interaction_pages/commandControlCharts';
import { categoricalColor } from './graph_components/chartTheme';

export const BEFORE_AFTER_COMPONENT = 'before_after_doughnut';

const sum = (values) => values.reduce((total, value) => total + value, 0);
const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/**
 * La phrase qui dit l'écart. La base est toujours l'état de départ (diviser par
 * l'état d'arrivée a déjà été un bug signalé en salle, cf. btpSummary.js).
 */
export function changeSentence(before, after) {
  if (!(before > 0)) return '';
  if (after === before) return "Aucun changement depuis le début de l'atelier.";
  const percent = Math.round((100 * Math.abs(before - after)) / before);
  if (percent === 0) return "Presque aucun changement depuis le début de l'atelier.";
  return `Soit ${percent} % de ${after < before ? 'moins' : 'plus'} qu'au début de l'atelier.`;
}

/**
 * Les deux états d'un score, prêts à dessiner, ou `null` s'il n'y a rien à
 * comparer (score absent, pas de ventilation, tout à zéro).
 *
 * LES CATÉGORIES SUIVENT L'ORDRE DE `standard`, pour les deux anneaux : une
 * couleur est attribuée par rang, et une `current` écrite dans un autre ordre
 * peindrait la même catégorie de deux couleurs. Une valeur négative est ramenée
 * à zéro, car un anneau ne sait pas la dessiner.
 */
export function beforeAfterOf(score, colorOffset = 0) {
  const standard = score?.metadata?.standard;
  const current = score?.metadata?.current;
  if (!standard || !current) return null;

  const labels = Object.keys(standard);
  const categories = labels.map((label, index) => ({
    label,
    before: Math.max(0, number(standard[label])),
    after: Math.max(0, number(current[label])),
    color: categoricalColor(colorOffset + index),
  }));
  const before = sum(categories.map((row) => row.before));
  const after = sum(categories.map((row) => row.after));
  if (Math.max(before, after) <= 0) return null;

  return {
    name: score.name || '',
    unit: score.unit || DEFAULT_UNIT,
    categories,
    before,
    after,
    // La même échelle des deux côtés : sans elle, les deux anneaux seraient
    // pleins et ne diraient plus rien. Le max couvre une mesure qui AUGMENTE
    // le score, qu'un arc de plus de 360° ne saurait pas montrer.
    scale: Math.max(before, after),
    colorOffset,
    summary: changeSentence(before, after),
  };
}

/** Une catégorie → sa valeur, la forme que `WaterUsageChart` attend. */
export function chartData(series, state) {
  return Object.fromEntries(series.categories.map((row) => [row.label, row[state]]));
}

/**
 * Les clés de capture pour le rapport (`data-report-chart`). Dérivées du score
 * et non du rang : déplacer une carte ne doit pas intervertir les images.
 */
export function reportChartKeys(scoreId) {
  return {
    before: `before_after:${scoreId}:before`,
    after: `before_after:${scoreId}:after`,
  };
}

/**
 * Les séries d'une liste de graphes, indexées par leur rang dans la liste.
 *
 * La palette reprend là où le graphe précédent s'est arrêté : « une couleur ne
 * dit qu'une chose sur l'écran », et la consommation et la pollution d'une même
 * section n'ont aucune catégorie commune.
 */
export function beforeAfterSeries(listGraph, scoreDic) {
  const series = {};
  let colorOffset = 0;
  (listGraph || []).forEach((item, index) => {
    if (item?.component_id !== BEFORE_AFTER_COMPONENT) return;
    const scoreId = item.metadata?.score_id;
    const one = beforeAfterOf(scoreDic?.[scoreId], colorOffset);
    if (!one) return;
    series[index] = { ...one, scoreId };
    colorOffset += one.categories.length;
  });
  return series;
}

/**
 * Les sections ajoutées en bas d'un bilan (`content.metadata.extra_sections`).
 * Toute forme inattendue est écartée sans bruit : le validateur le dit à
 * l'auteur, et un écran de fin ne plante pas devant une salle.
 */
export function extraSectionsOf(content) {
  const declared = content?.metadata?.extra_sections;
  if (!Array.isArray(declared)) return [];
  return declared
    .filter((section) => Array.isArray(section?.list_graph) && section.list_graph.length > 0)
    .map((section) => ({
      title: section.title || '',
      hint: section.hint || '',
      list_graph: section.list_graph,
    }));
}

/**
 * Ce que ces sections envoient au rapport. SEULS LES AVANT/APRÈS Y PARTENT : le
 * serveur sait les composer, et une section qui n'en contient aucun ne laisse
 * pas de titre orphelin dans le document.
 */
export function reportSectionsOf(content, scoreDic) {
  return extraSectionsOf(content)
    .map((section) => ({
      title: section.title,
      hint: section.hint,
      comparisons: Object.values(beforeAfterSeries(section.list_graph, scoreDic)).map((one) => {
        const keys = reportChartKeys(one.scoreId);
        return {
          name: one.name,
          summary: one.summary,
          unit: one.unit,
          before_chart: keys.before,
          after_chart: keys.after,
          // La couleur part avec la donnée : le serveur ne la choisit pas.
          categories: one.categories,
        };
      }),
    }))
    .filter((section) => section.comparisons.length > 0);
}
